const DEFAULT_STYLE = 'radiationStyle';
import { Ref, WatchStopHandle } from 'vue';
import omit from 'lodash/omit';
import { LAYER_CONFIG_OMIT_PROP } from '../constants';


export default function useRadiationLayer(mapInstance: LooseObject, layerInfo: Ref<MapLayer<RadiationLayerConfig>>) {
  if (!layerInfo.value || !layerInfo.value.config || !TMap.visualization?.Radiation) return;

  const options = omit(layerInfo.value.config, LAYER_CONFIG_OMIT_PROP);

  let unwatchList: WatchStopHandle[] | null = [];

  const radiationOption = {
    number: options.number,
    enableBloom: options.enableBloom,
    styles: {
      [DEFAULT_STYLE]: {
        fillColor: options.fillColor || 'rgba(56, 124, 234, 0.7)',
        strokeColor: options.strokeColor || 'rgb(103, 153, 234)',
        strokeWidth: options.strokeWidth || 1,
      },
    },
    processAnimation: {
      duration: options.duration || 2000,
    },
    minZoom: options.zoomRange?.[0],
    maxZoom: options.zoomRange?.[1],
  };

  const formatData = (data: Radiation[]) => data.map((item: Radiation) => ({
    center: new TMap.LatLng(item.lat, item.lng),
    styleId: DEFAULT_STYLE,
    radius: item.radius,
  }));

  const radiationLayer = new TMap.visualization.Radiation(radiationOption);
  radiationLayer.addTo(mapInstance);

  unwatchList.push(watchEffect(() => {
    if (!Array.isArray(layerInfo.value.config.data)) return;
    try {
      const radiationData = formatData(layerInfo.value.config.data);
      radiationLayer?.setData(radiationData);
    } catch (error) {
      radiationLayer?.setData([]);
    }
  }));

  unwatchList.push(watch([
    () => layerInfo.value.config.fillColor,
    () => layerInfo.value.config.strokeColor,
    () => layerInfo.value.config.strokeWidth,
  ], (newVal) => {
    radiationLayer.setStyles({
      [DEFAULT_STYLE]: {
        fillColor: newVal[0] || 'rgba(56, 124, 234, 0.7)',
        strokeColor: newVal[1] || 'rgb(103, 153, 234)',
        strokeWidth: newVal[2] || 1,
      },
    });
  }));

  unwatchList.push(watch(() => layerInfo.value.config.duration, (newVal) => {
    radiationLayer.updateAnimation('process', { duration: newVal });
  }));

  unwatchList.push(watch(() => layerInfo.value.config.zoomRange, (newVal) => {
    if (!newVal) return;
    radiationLayer.setMinZoom(newVal[0]);
    radiationLayer.setMinZoom(newVal[1]);
  }));

  unwatchList.push(watch([
    () => layerInfo.value.config.enableBloom,
    () => layerInfo.value.config.number,
  ], () => {
    radiationLayer?.unmount();
    useRadiationLayer(mapInstance, layerInfo);
  }));

  radiationLayer.unmount = (): void => {
    radiationLayer?.destroy();
    unwatchList?.forEach(unwatch => unwatch());
    unwatchList = null;
  };

  return radiationLayer;
}
